import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { updatePreferences } from '../api';

const DEFAULT_PREFERENCES = {
  theme: 'light',
  language: 'de',
  rememberSidebar: true,
  animationsEnabled: true,
  fontSize: 'normal',
};

const THEME_OPTIONS = ['light', 'classic', 'high-contrast'];
const FONT_SIZE_OPTIONS = ['normal', 'large', 'x-large'];
const LANGUAGE_OPTIONS = ['de', 'en'];

function InterfaceSettingsForm({
  preferences,
  onChanged,
}) {
  const { t } = useTranslation();

  const [theme, setTheme] = useState(
    DEFAULT_PREFERENCES.theme,
  );

  const [language, setLanguage] = useState(
    DEFAULT_PREFERENCES.language,
  );

  const [rememberSidebar, setRememberSidebar] =
    useState(DEFAULT_PREFERENCES.rememberSidebar);

  const [animationsEnabled, setAnimationsEnabled] =
    useState(DEFAULT_PREFERENCES.animationsEnabled);

  const [fontSize, setFontSize] = useState(
    DEFAULT_PREFERENCES.fontSize,
  );

  const [error, setError] = useState('');
  const [savedField, setSavedField] = useState('');

  useEffect(() => {
    if (!preferences) {
      return;
    }

    setTheme(
      THEME_OPTIONS.includes(preferences.theme)
        ? preferences.theme
        : DEFAULT_PREFERENCES.theme,
    );

    setLanguage(
      LANGUAGE_OPTIONS.includes(preferences.language)
        ? preferences.language
        : DEFAULT_PREFERENCES.language,
    );

    setRememberSidebar(
      preferences.rememberSidebar ??
        DEFAULT_PREFERENCES.rememberSidebar,
    );

    setAnimationsEnabled(
      preferences.animationsEnabled ??
        DEFAULT_PREFERENCES.animationsEnabled,
    );

    setFontSize(
      FONT_SIZE_OPTIONS.includes(preferences.fontSize)
        ? preferences.fontSize
        : DEFAULT_PREFERENCES.fontSize,
    );
  }, [preferences]);

  useEffect(() => {
    if (!savedField) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setSavedField('');
    }, 2000);

    return () => {
      window.clearTimeout(timer);
    };
  }, [savedField]);

  async function save(field, value, revert) {
    try {
      setError('');

      await updatePreferences({ [field]: value });

      setSavedField(field);

      await onChanged();
    } catch (submitError) {
      console.error(
        'Failed to save interface preference:',
        submitError,
      );

      setError(
        submitError.message ||
          t('interfaceSettings.errorGeneric'),
      );

      revert();
    }
  }

  return (
    <div className="account-preferences">
      {error && (
        <div
          className="login-message login-message-error"
          role="alert"
        >
          <i className="fa-solid fa-circle-exclamation" />
          <span>{error}</span>
        </div>
      )}

      <label className="account-choice">
        <span>{t('interfaceSettings.theme')}</span>

        <select
          value={theme}
          onChange={(event) => {
            const previous = theme;
            const next = event.target.value;

            setTheme(next);

            save('theme', next, () => {
              setTheme(previous);
            });
          }}
        >
          <option value="light">{t('interfaceSettings.themeLight')}</option>
          <option value="classic">{t('interfaceSettings.themeClassic')}</option>
          <option value="high-contrast">{t('interfaceSettings.themeHighContrast')}</option>
        </select>
      </label>

      <label className="account-choice">
        <span>{t('interfaceSettings.fontSize')}</span>

        <select
          value={fontSize}
          onChange={(event) => {
            const previous = fontSize;
            const next = event.target.value;

            setFontSize(next);

            save('fontSize', next, () => {
              setFontSize(previous);
            });
          }}
        >
          <option value="normal">{t('interfaceSettings.fontSizeNormal')}</option>
          <option value="large">{t('interfaceSettings.fontSizeLarge')}</option>
          <option value="x-large">{t('interfaceSettings.fontSizeExtraLarge')}</option>
        </select>
      </label>

      <label className="account-choice">
        <span>{t('interfaceSettings.language')}</span>

        <select
          value={language}
          onChange={(event) => {
            const previous = language;
            const next = event.target.value;

            setLanguage(next);

            save('language', next, () => {
              setLanguage(previous);
            });
          }}
        >
          <option value="de">{t('interfaceSettings.languageGerman')}</option>
          <option value="en">{t('interfaceSettings.languageEnglish')}</option>
        </select>
      </label>

      <label className="account-toggle">
        <input
          type="checkbox"
          checked={rememberSidebar}
          onChange={(event) => {
            const previous = rememberSidebar;
            const next = event.target.checked;

            setRememberSidebar(next);

            save('rememberSidebar', next, () => {
              setRememberSidebar(previous);
            });
          }}
        />

        <span>{t('interfaceSettings.rememberSidebar')}</span>
      </label>

      <label className="account-toggle">
        <input
          type="checkbox"
          checked={animationsEnabled}
          onChange={(event) => {
            const previous = animationsEnabled;
            const next = event.target.checked;

            setAnimationsEnabled(next);

            save('animationsEnabled', next, () => {
              setAnimationsEnabled(previous);
            });
          }}
        />

        <span>{t('interfaceSettings.enableAnimations')}</span>
      </label>

      {savedField && (
        <div
          className="login-message login-message-success account-preferences-saved"
          role="status"
        >
          <i className="fa-solid fa-circle-check" />
          <span>{t('interfaceSettings.saved')}</span>
        </div>
      )}
    </div>
  );
}

export default InterfaceSettingsForm;
